import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Alert, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import { GripVertical, Trash2 } from "lucide-react";
import { applyProductNameNormalize } from "../lib/productNameNormalize/engine.js";
import {
    cloneDefaultProductNameNormalizeConfig,
    setProductNameNormalizeRuntimeConfig,
} from "../lib/productNameNormalize/runtime.js";
import ProductNameNormalizeRulesDragLayer from "./ProductNameNormalizeRulesDragLayer.jsx";
import "./ProductNameNormalizeSettingsPage.css";

function newRuleId() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `r-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function sanitizeConfig(config) {
    const c = JSON.parse(JSON.stringify(config));
    c.trimAfterDash = !!c.trimAfterDash;
    c.stripTrailingYear = !!c.stripTrailingYear;
    c.rules = (c.rules || []).map((rule) => {
        const r = { ...rule, id: rule.id || newRuleId(), result: String(rule.result || "").trim() };
        if (r.kind === "includes") {
            r.pattern = String(r.pattern || "").trim();
        }
        if (r.kind === "exactList") {
            r.patterns = (r.patterns || [])
                .map((p) => String(p || "").trim())
                .filter(Boolean);
        }
        if (r.kind === "any") {
            r.clauses = (r.clauses || []).map((cl) => {
                if (cl.kind === "includes") {
                    return { kind: "includes", pattern: String(cl.pattern || "").trim() };
                }
                if (cl.kind === "exact") {
                    return { kind: "exact", value: String(cl.value || "").trim() };
                }
                return cl;
            });
        }
        return r;
    });
    return c;
}

const emptyIncludesRule = () => ({
    id: newRuleId(),
    kind: "includes",
    pattern: "",
    result: "",
});

const emptyExactListRule = () => ({
    id: newRuleId(),
    kind: "exactList",
    patterns: [""],
    result: "",
});

const emptyAnyRule = () => ({
    id: newRuleId(),
    kind: "any",
    clauses: [{ kind: "includes", pattern: "" }],
    result: "",
});

function RuleTableRow({
    rule,
    index,
    isAdmin,
    draggingId,
    onReorderPointerDown,
    updateRule,
    updateClause,
    addClause,
    removeClause,
    removeRule,
}) {
    const handleRef = useRef(null);
    const isCollapsed = rule.id === draggingId;

    const rowClass = "pnn-rule-row";

    const applyKindChange = (k) => {
        if (k === "includes") {
            updateRule(index, { kind: "includes", pattern: "", clauses: undefined, patterns: undefined });
        } else if (k === "exactList") {
            updateRule(index, { kind: "exactList", patterns: [""], pattern: undefined, clauses: undefined });
        } else {
            updateRule(index, {
                kind: "any",
                clauses: [{ kind: "includes", pattern: "" }],
                pattern: undefined,
                patterns: undefined,
            });
        }
    };

    const onHandlePointerDown = (e) => {
        if (!isAdmin || e.button !== 0) return;
        onReorderPointerDown(e, rule, index, handleRef.current);
    };

    if (isCollapsed) {
        return (
            <tr className={`${rowClass} pnn-rule-row--collapsed-source`} data-pnn-rule-index={index}>
                <td colSpan={5} className="p-0">
                    <div className="pnn-collapsed-inner d-flex align-items-center gap-2 px-3 py-2">
                        <span
                            ref={handleRef}
                            className="pnn-drag-handle"
                            title="Відпустіть над пунктирним слотом"
                            onPointerDown={onHandlePointerDown}
                        >
                            <GripVertical size={20} strokeWidth={2} />
                        </span>
                        <span className="small text-muted mb-0">
                            Місце звідси забрано — картка під пальцем / курсором. Відпустіть над пунктиром.
                        </span>
                    </div>
                </td>
            </tr>
        );
    }

    return (
        <tr className={rowClass} data-pnn-rule-index={index}>
            <td className="pnn-col-num align-middle">{index + 1}</td>
            <td className="pnn-col-condition">
                <Form.Select
                    size="sm"
                    value={rule.kind}
                    disabled={!isAdmin}
                    onChange={(e) => applyKindChange(e.target.value)}
                    className="border-secondary"
                >
                    <option value="includes">Містить підрядок</option>
                    <option value="exactList">Точний збіг (список)</option>
                    <option value="any">Будь-яка з умов (АБО)</option>
                </Form.Select>
            </td>
            <td>
                {rule.kind === "includes" && (
                    <Form.Control
                        size="sm"
                        value={rule.pattern || ""}
                        disabled={!isAdmin}
                        onChange={(e) => updateRule(index, { pattern: e.target.value })}
                        placeholder="Будь-який регістр — збіг без урахування великих/малих літер"
                    />
                )}
                {rule.kind === "exactList" && (
                    <Form.Control
                        as="textarea"
                        rows={Math.min(7, Math.max(2, (rule.patterns || []).length || 2))}
                        size="sm"
                        value={(rule.patterns || []).join("\n")}
                        disabled={!isAdmin}
                        onChange={(e) =>
                            updateRule(index, {
                                patterns: e.target.value.split("\n"),
                            })
                        }
                        placeholder="Один варіант на рядок (регістр довільний)"
                        className="font-monospace small"
                    />
                )}
                {rule.kind === "any" && (
                    <div>
                        {(rule.clauses || []).map((cl, ci) => (
                            <Row key={ci} className="g-2 mb-2 align-items-center">
                                <Col xs="auto">
                                    <Form.Select
                                        size="sm"
                                        style={{ width: 128 }}
                                        value={cl.kind}
                                        disabled={!isAdmin}
                                        onChange={(e) =>
                                            updateClause(index, ci, {
                                                kind: e.target.value,
                                                pattern: "",
                                                value: "",
                                            })
                                        }
                                    >
                                        <option value="includes">Підрядок</option>
                                        <option value="exact">Точно</option>
                                    </Form.Select>
                                </Col>
                                <Col>
                                    <Form.Control
                                        size="sm"
                                        placeholder={
                                            cl.kind === "exact"
                                                ? "Повна назва (будь-який регістр)"
                                                : "Фрагмент (будь-який регістр)"
                                        }
                                        value={cl.kind === "exact" ? cl.value || "" : cl.pattern || ""}
                                        disabled={!isAdmin}
                                        onChange={(e) =>
                                            updateClause(
                                                index,
                                                ci,
                                                cl.kind === "exact"
                                                    ? { value: e.target.value }
                                                    : { pattern: e.target.value }
                                            )
                                        }
                                    />
                                </Col>
                                {isAdmin && (
                                    <Col xs="auto">
                                        <Button
                                            size="sm"
                                            variant="outline-danger"
                                            className="px-2"
                                            onClick={() => removeClause(index, ci)}
                                            disabled={(rule.clauses || []).length < 2}
                                            title="Прибрати підумову"
                                        >
                                            ×
                                        </Button>
                                    </Col>
                                )}
                            </Row>
                        ))}
                        {isAdmin && (
                            <Button size="sm" variant="outline-secondary" onClick={() => addClause(index)}>
                                + підумова
                            </Button>
                        )}
                    </div>
                )}
            </td>
            <td style={{ minWidth: 160 }}>
                <Form.Control
                    size="sm"
                    value={rule.result || ""}
                    disabled={!isAdmin}
                    onChange={(e) => updateRule(index, { result: e.target.value })}
                    placeholder="Як показувати в звітах"
                />
            </td>
            <td className="pnn-col-actions text-center align-middle">
                <div className="d-flex flex-column align-items-center gap-2 py-1">
                    <span
                        ref={handleRef}
                        className={`pnn-drag-handle ${!isAdmin ? "pnn-drag-handle--disabled" : ""}`}
                        title={
                            isAdmin
                                ? "Натисніть і ведіть трекпадом / мишею (без HTML5-drag — стабільно на Mac)"
                                : ""
                        }
                        aria-hidden={!isAdmin}
                        onPointerDown={isAdmin ? onHandlePointerDown : undefined}
                    >
                        <GripVertical size={20} strokeWidth={2} />
                    </span>
                    {isAdmin && (
                        <Button
                            size="sm"
                            variant="outline-danger"
                            className="p-1 d-flex align-items-center justify-content-center"
                            style={{ width: 34, height: 34 }}
                            onClick={() => removeRule(rule.id)}
                            title="Видалити правило"
                        >
                            <Trash2 size={16} />
                        </Button>
                    )}
                </div>
            </td>
        </tr>
    );
}

export default function ProductNameNormalizeSettingsPage({ user }) {
    const isAdmin = user?.role === "admin";
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [persisted, setPersisted] = useState(false);
    const [draft, setDraft] = useState(() => sanitizeConfig(cloneDefaultProductNameNormalizeConfig()));
    const [previewIn, setPreviewIn] = useState("Карго 2024 - колір червоний");
    const [draggingId, setDraggingId] = useState(null);
    const [insertBeforeIndex, setInsertBeforeIndex] = useState(0);

    const [pointerDragActive, setPointerDragActive] = useState(false);
    const [pointerPos, setPointerPos] = useState({ x: 0, y: 0 });
    const [dragSnapshot, setDragSnapshot] = useState(null);
    const [grabOffset, setGrabOffset] = useState({ x: 24, y: 32 });

    const insertBeforeRef = useRef(0);
    const draggingIdRef = useRef(null);
    const activePointerIdRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await axios.get("/api/product-name-normalize-config", { withCredentials: true });
            if (res.data?.config) {
                setDraft(sanitizeConfig(res.data.config));
                setPersisted(!!res.data.persisted);
            }
        } catch (e) {
            setError("Не вдалося завантажити налаштування");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const previewOut = useMemo(
        () => applyProductNameNormalize(previewIn, sanitizeConfig(draft)),
        [previewIn, draft]
    );

    const updateRule = useCallback((idx, patch) => {
        setDraft((prev) => {
            const rules = [...prev.rules];
            rules[idx] = { ...rules[idx], ...patch };
            return { ...prev, rules };
        });
    }, []);

    const updateClause = useCallback((ruleIndex, clauseIndex, patch) => {
        setDraft((prev) => {
            const rules = [...prev.rules];
            const rule = { ...rules[ruleIndex] };
            const clauses = [...(rule.clauses || [])];
            clauses[clauseIndex] = { ...clauses[clauseIndex], ...patch };
            rule.clauses = clauses;
            rules[ruleIndex] = rule;
            return { ...prev, rules };
        });
    }, []);

    const addClause = useCallback((ruleIndex) => {
        setDraft((prev) => {
            const rules = [...prev.rules];
            const rule = { ...rules[ruleIndex] };
            rule.clauses = [...(rule.clauses || []), { kind: "includes", pattern: "" }];
            rules[ruleIndex] = rule;
            return { ...prev, rules };
        });
    }, []);

    const removeClause = useCallback((ruleIndex, clauseIndex) => {
        setDraft((prev) => {
            const rules = [...prev.rules];
            const rule = { ...rules[ruleIndex] };
            rule.clauses = (rule.clauses || []).filter((_, i) => i !== clauseIndex);
            rules[ruleIndex] = rule;
            return { ...prev, rules };
        });
    }, []);

    const commitReorder = useCallback((ruleId, insertBefore) => {
        setDraft((prev) => {
            const rules = [...prev.rules];
            const from = rules.findIndex((r) => r.id === ruleId);
            if (from < 0) return prev;
            const [item] = rules.splice(from, 1);
            let to = insertBefore;
            if (from < to) to -= 1;
            to = Math.max(0, Math.min(to, rules.length));
            rules.splice(to, 0, item);
            return { ...prev, rules };
        });
    }, []);

    const removeRule = useCallback((ruleId) => {
        setDraft((prev) => ({
            ...prev,
            rules: prev.rules.filter((r) => r.id !== ruleId),
        }));
    }, []);

    const addRule = useCallback((template) => {
        setDraft((prev) => ({
            ...prev,
            rules: [...prev.rules, template()],
        }));
    }, []);

    const updateInsertFromPoint = useCallback((clientX, clientY) => {
        if (!draggingIdRef.current) return;
        const el = document.elementFromPoint(clientX, clientY);
        if (!el) return;

        const slotTr = el.closest("[data-pnn-slot-before]");
        if (slotTr) {
            const i = Number(slotTr.getAttribute("data-pnn-slot-before"));
            if (!Number.isNaN(i) && insertBeforeRef.current !== i) {
                insertBeforeRef.current = i;
                setInsertBeforeIndex(i);
            }
            return;
        }

        const ruleTr = el.closest("[data-pnn-rule-index]");
        if (ruleTr) {
            const i = Number(ruleTr.getAttribute("data-pnn-rule-index"));
            if (Number.isNaN(i)) return;
            const rect = ruleTr.getBoundingClientRect();
            const mid = (rect.bottom - rect.top) / 2;
            const y = clientY - rect.top;
            const next = y < mid ? i : i + 1;
            if (insertBeforeRef.current !== next) {
                insertBeforeRef.current = next;
                setInsertBeforeIndex(next);
            }
        }
    }, []);

    const finishPointerReorder = useCallback(() => {
        const id = draggingIdRef.current;
        const at = insertBeforeRef.current;
        draggingIdRef.current = null;
        setDraggingId(null);
        setInsertBeforeIndex(0);
        insertBeforeRef.current = 0;
        activePointerIdRef.current = null;
        setPointerDragActive(false);
        setDragSnapshot(null);
        if (id) {
            commitReorder(id, at);
        }
    }, [commitReorder]);

    const updateInsertRef = useRef(updateInsertFromPoint);
    updateInsertRef.current = updateInsertFromPoint;
    const finishPointerRef = useRef(finishPointerReorder);
    finishPointerRef.current = finishPointerReorder;

    useEffect(() => {
        if (!pointerDragActive) return undefined;

        const pid = activePointerIdRef.current;
        const opts = { capture: true };

        const onMove = (e) => {
            if (e.pointerId !== pid) return;
            if (e.cancelable) e.preventDefault();
            setPointerPos({ x: e.clientX, y: e.clientY });
            updateInsertRef.current(e.clientX, e.clientY);
        };

        const onEnd = (e) => {
            if (e.pointerId !== pid) return;
            if (e.cancelable) e.preventDefault();
            finishPointerRef.current();
        };

        window.addEventListener("pointermove", onMove, opts);
        window.addEventListener("pointerup", onEnd, opts);
        window.addEventListener("pointercancel", onEnd, opts);

        const prevUserSelect = document.body.style.userSelect;
        const prevCursor = document.body.style.cursor;
        document.body.style.userSelect = "none";
        document.body.style.cursor = "grabbing";

        return () => {
            window.removeEventListener("pointermove", onMove, opts);
            window.removeEventListener("pointerup", onEnd, opts);
            window.removeEventListener("pointercancel", onEnd, opts);
            document.body.style.userSelect = prevUserSelect;
            document.body.style.cursor = prevCursor;
        };
    }, [pointerDragActive]);

    const onReorderPointerDown = useCallback((e, rule, index, handleEl) => {
        if (!isAdmin || e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();

        const rect = handleEl?.getBoundingClientRect?.() || { left: e.clientX, top: e.clientY };
        const grabX = Math.min(48, Math.max(12, e.clientX - rect.left));
        const grabY = Math.min(40, Math.max(8, e.clientY - rect.top));

        activePointerIdRef.current = e.pointerId;
        try {
            handleEl?.setPointerCapture?.(e.pointerId);
        } catch {
            /* ignore */
        }

        draggingIdRef.current = rule.id;
        setDraggingId(rule.id);
        insertBeforeRef.current = index;
        setInsertBeforeIndex(index);
        setGrabOffset({ x: grabX + 8, y: grabY + 12 });
        setDragSnapshot(JSON.parse(JSON.stringify(rule)));
        setPointerPos({ x: e.clientX, y: e.clientY });
        setPointerDragActive(true);
    }, [isAdmin]);

    const handleSave = async () => {
        if (!isAdmin) return;
        const body = sanitizeConfig(draft);
        setSaving(true);
        setError("");
        try {
            await axios.put("/api/product-name-normalize-config", { config: body }, { withCredentials: true });
            setProductNameNormalizeRuntimeConfig(body);
            setPersisted(true);
        } catch (e) {
            const msg = e.response?.data?.error || e.message || "Помилка збереження";
            setError(msg);
            if (e.response?.data?.details?.length) {
                setError(`${msg}: ${e.response.data.details.join("; ")}`);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleResetLocal = () => {
        const def = sanitizeConfig(cloneDefaultProductNameNormalizeConfig());
        setDraft(def);
        setProductNameNormalizeRuntimeConfig(def);
    };

    if (loading) {
        return <p className="p-3">Завантаження…</p>;
    }

    const hasActiveDrag = Boolean(draggingId);

    return (
        <div className="container py-4">
            <ProductNameNormalizeRulesDragLayer
                active={pointerDragActive}
                clientX={pointerPos.x}
                clientY={pointerPos.y}
                grabOffsetX={grabOffset.x}
                grabOffsetY={grabOffset.y}
                snapshot={dragSnapshot}
            />

            <h1 className="h4 mb-3">Нормалізація назв товарів</h1>
            <p className="text-muted small mb-4">
                Правила зверху вниз: перше збіг — результат. За ⋮⋮ натисніть і ведіть (трекпад або миша) — використовуються
                pointer-події, без «класичного» HTML5-drag (на Mac з трекпадом він часто зривається). Порівняння текстів{" "}
                <strong>без урахування регістру</strong>.
            </p>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError("")}>
                    {error}
                </Alert>
            )}

            <Card className="mb-4 border-0 shadow-sm">
                <Card.Body>
                    <Card.Title className="h6">Попередня обробка</Card.Title>
                    <Form.Check
                        type="checkbox"
                        id="trimAfterDash"
                        label='Обрізати все після першого " - "'
                        checked={!!draft.trimAfterDash}
                        disabled={!isAdmin}
                        onChange={(e) => setDraft((p) => ({ ...p, trimAfterDash: e.target.checked }))}
                        className="mb-2"
                    />
                    <Form.Check
                        type="checkbox"
                        id="stripTrailingYear"
                        label="Прибирати 4 цифри року в кінці (наприклад Карго 2024 → Карго)"
                        checked={!!draft.stripTrailingYear}
                        disabled={!isAdmin}
                        onChange={(e) => setDraft((p) => ({ ...p, stripTrailingYear: e.target.checked }))}
                    />
                </Card.Body>
            </Card>

            <Card className="mb-4 border-0 shadow-sm">
                <Card.Body>
                    <Card.Title className="h6">Перевірка на прикладі</Card.Title>
                    <Row className="g-2 align-items-end">
                        <Col md={6}>
                            <Form.Label className="small text-muted">Вхідна назва</Form.Label>
                            <Form.Control
                                value={previewIn}
                                onChange={(e) => setPreviewIn(e.target.value)}
                                placeholder="Сира назва з CRM / Shopify"
                            />
                        </Col>
                        <Col md={6}>
                            <Form.Label className="small text-muted">Після нормалізації</Form.Label>
                            <Form.Control readOnly value={previewOut} className="bg-light" />
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <div className="d-flex flex-wrap gap-2 mb-3">
                {isAdmin && (
                    <>
                        <Button variant="primary" onClick={handleSave} disabled={saving}>
                            {saving ? "Збереження…" : "Зберегти в базу"}
                        </Button>
                        <Button variant="outline-secondary" onClick={handleResetLocal} disabled={saving}>
                            Скинути форму до дефолту (у пам&apos;яті)
                        </Button>
                    </>
                )}
                <Button variant="outline-primary" onClick={load} disabled={saving}>
                    Перезавантажити з сервера
                </Button>
                <span className="align-self-center small text-muted">
                    {persisted ? "Є збережена копія в MongoDB" : "Ще не зберігали — працює вбудований дефолт"}
                </span>
            </div>

            {!isAdmin && (
                <Alert variant="info">Редагування, збереження та зміна порядку доступні лише для ролі admin.</Alert>
            )}

            <h2 className="h6 mb-2">Правила ({draft.rules.length})</h2>

            {isAdmin && (
                <div className="d-flex flex-wrap gap-2 mb-3">
                    <Button size="sm" variant="outline-dark" onClick={() => addRule(emptyIncludesRule)}>
                        + містить підрядок
                    </Button>
                    <Button size="sm" variant="outline-dark" onClick={() => addRule(emptyExactListRule)}>
                        + точний збіг (список)
                    </Button>
                    <Button size="sm" variant="outline-dark" onClick={() => addRule(emptyAnyRule)}>
                        + умова АБО
                    </Button>
                </div>
            )}

            <div className={`pnn-rules-wrap mb-4 ${hasActiveDrag ? "pnn-rules-wrap--dragging" : ""}`}>
                <Table responsive className="pnn-rules-table align-middle mb-0">
                    <thead>
                        <tr>
                            <th className="pnn-col-num">№</th>
                            <th className="pnn-col-condition">Умова</th>
                            <th>
                                Назва / шаблон
                                <div className="pnn-th-hint">
                                    Регістр не впливає на збіг. Один варіант на рядок для точного списку або АБО.
                                </div>
                            </th>
                            <th style={{ minWidth: 160 }}>Результат</th>
                            <th className="pnn-col-actions">Дії</th>
                        </tr>
                    </thead>
                    <tbody>
                        {draft.rules.map((rule, i) => (
                            <Fragment key={rule.id}>
                                {hasActiveDrag && insertBeforeIndex === i && (
                                    <tr
                                        className="pnn-slot-row"
                                        data-pnn-slot-before={i}
                                        aria-hidden
                                    >
                                        <td colSpan={5}>
                                            <div className="pnn-slot-row__inner">
                                                <span className="pnn-slot-row__label">Відпустіть тут</span>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                <RuleTableRow
                                    rule={rule}
                                    index={i}
                                    isAdmin={isAdmin}
                                    draggingId={draggingId}
                                    onReorderPointerDown={onReorderPointerDown}
                                    updateRule={updateRule}
                                    updateClause={updateClause}
                                    addClause={addClause}
                                    removeClause={removeClause}
                                    removeRule={removeRule}
                                />
                            </Fragment>
                        ))}
                        {hasActiveDrag && insertBeforeIndex === draft.rules.length && (
                            <tr
                                className="pnn-slot-row"
                                data-pnn-slot-before={draft.rules.length}
                                aria-hidden
                            >
                                <td colSpan={5}>
                                    <div className="pnn-slot-row__inner">
                                        <span className="pnn-slot-row__label">Відпустіть в кінець списку</span>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>
        </div>
    );
}
